import { Button, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalyticsConsent } from '@context/AnalyticsConsentContext';
import { AppPrimaryButton } from '@components/ui/AppPrimaryButton/AppPrimaryButton';
import { AppAnalyticsConsentBanner } from '@components/ui/AppAnalyticsConsentBanner/AppAnalyticsConsentBanner';
import styles from './AnalyticsConsentControls.module.css';

const PRIVACY_POLICY_URL = 'https://bundledrop.app/privacy-policy';
const BANNER_REVEAL_DELAY_MS = 1800;

const ConsentActions = () => {
  const { t } = useTranslation();
  const { denyConsent, grantConsent } = useAnalyticsConsent();

  return (
    <div className={styles.actions}>
      <AppPrimaryButton type="default" onClick={denyConsent} className={styles.actionButton}>
        {t('analyticsConsent.decline')}
      </AppPrimaryButton>
      <AppPrimaryButton type="primary" onClick={grantConsent} className={styles.actionButton}>
        {t('analyticsConsent.allow')}
      </AppPrimaryButton>
    </div>
  );
};

const AnalyticsConsentBanner = () => {
  const { t } = useTranslation();
  const { canOfferAnalytics, denyConsent, grantConsent, isPreferencesOpen, status } =
    useAnalyticsConsent();
  const [isRevealDelayComplete, setIsRevealDelayComplete] = useState(false);
  const shouldOfferPrompt = canOfferAnalytics && status === 'unknown';
  const shouldShow = shouldOfferPrompt && isRevealDelayComplete && !isPreferencesOpen;

  useEffect(() => {
    const revealTimer = window.setTimeout(
      () => {
        setIsRevealDelayComplete(shouldOfferPrompt);
      },
      shouldOfferPrompt ? BANNER_REVEAL_DELAY_MS : 0
    );

    return () => window.clearTimeout(revealTimer);
  }, [shouldOfferPrompt]);

  return (
    <AppAnalyticsConsentBanner
      open={shouldShow}
      ariaLabel={t('analyticsConsent.bannerAriaLabel')}
      eyebrow={t('analyticsConsent.eyebrow')}
      title={t('analyticsConsent.bannerTitle')}
      description={
        <>
          {t('analyticsConsent.bannerDescription')}{' '}
          <a
            className={styles.privacyLink}
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
          >
            {t('analyticsConsent.privacyPolicy')}
          </a>
          .
        </>
      }
      declineLabel={t('analyticsConsent.decline')}
      allowLabel={t('analyticsConsent.allow')}
      onDecline={denyConsent}
      onAllow={grantConsent}
    />
  );
};

const AnalyticsPreferencesModal = () => {
  const { t } = useTranslation();
  const { canOfferAnalytics, closePreferences, isPreferencesOpen, status } = useAnalyticsConsent();

  if (!canOfferAnalytics) {
    return null;
  }

  return (
    <Modal
      open={isPreferencesOpen}
      onCancel={closePreferences}
      footer={null}
      destroyOnHidden
      title={t('analyticsConsent.preferencesTitle')}
      classNames={{
        mask: styles.modalMask,
        content: styles.modalContent,
        header: styles.modalHeader,
        body: styles.modalBody,
      }}
    >
      <div className={styles.modalStack}>
        <p className={styles.description}>{t('analyticsConsent.preferencesDescription')}</p>
        <p className={styles.currentPreference} aria-live="polite">
          <span>{t('analyticsConsent.currentPreference')}</span>{' '}
          <strong>{t(`analyticsConsent.status.${status}`)}</strong>
        </p>
        <a
          className={styles.privacyLink}
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noreferrer"
        >
          {t('analyticsConsent.privacyPolicy')}
        </a>
        <ConsentActions />
        <Button type="text" className={styles.closeButton} onClick={closePreferences}>
          {t('common.cancel')}
        </Button>
      </div>
    </Modal>
  );
};

export const AnalyticsConsentControls = () => (
  <>
    <AnalyticsConsentBanner />
    <AnalyticsPreferencesModal />
  </>
);
